import { useEffect, useState, useRef } from 'react';
import { Card, Typography, Table, Tag, Button, Modal, InputNumber, Form, Row, Col, Statistic, Empty, Space, Alert, message, Steps, Spin, Result } from 'antd';
import { DollarOutlined, CheckCircleOutlined, ClockCircleOutlined, StopOutlined, CreditCardOutlined, ReloadOutlined, MobileOutlined } from '@ant-design/icons';
import api from '../../../api/axios';

const { Title, Text } = Typography;

const formatPrice = (p) => {
    if (!p && p !== 0) return '0';
    return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export default function TarifStatus() {
    const [data, setData] = useState({ services: [], business: null });
    const [loading, setLoading] = useState(false);
    const [payModal, setPayModal] = useState(false);
    const [payRecord, setPayRecord] = useState(null);
    const [payForm] = Form.useForm();
    const [payLoading, setPayLoading] = useState(false);

    // Click oqimi: 'select' -> 'pending' -> 'success'
    const [step, setStep] = useState('select');
    const [invoice, setInvoice] = useState(null);
    const [polling, setPolling] = useState(false);
    const pollRef = useRef(null);

    const load = () => {
        setLoading(true);
        api.get('/tariffs/my-services-status').then(res => {
            setData(res.data.data || { services: [], business: null });
        }).catch(err => {
            console.error(err);
            message.error("Tarif holatini yuklashda xatolik yuz berdi");
        }).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    // Polling tozalash
    const stopPolling = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setPolling(false);
    };
    useEffect(() => () => stopPolling(), []);

    const openPay = (record) => {
        setPayRecord(record);
        setStep('select');
        setInvoice(null);
        payForm.setFieldsValue({ months: 1 });
        setPayModal(true);
    };

    const closePay = () => {
        stopPolling();
        setPayModal(false);
        setInvoice(null);
        setStep('select');
    };

    // 1-bosqich: invoice yaratish (status=pending) — xizmat hali faollashmaydi
    const handleStartPayment = (values) => {
        setPayLoading(true);
        api.post('/click/invoice', {
            services_id: payRecord.id,
            months: values.months,
        }).then(res => {
            const inv = res.data.data;
            setInvoice(inv);
            setStep('pending');
            // Real rejimda Click checkout sahifasini ochamiz + holatni so'roqlaymiz
            if (!inv.sandbox && inv.pay_url) {
                window.open(inv.pay_url, '_blank', 'noopener');
                startPolling(inv.merchant_trans_id);
            }
        }).catch(err => {
            const msg = err.response?.data?.message || "To'lovni boshlashda xatolik";
            Modal.error({ title: 'Xatolik', content: msg });
        }).finally(() => setPayLoading(false));
    };

    // Holatni davriy so'roqlash (real Click tasdiqini kutish)
    const startPolling = (merchantTransId) => {
        setPolling(true);
        pollRef.current = setInterval(() => {
            api.get(`/click/status/${merchantTransId}`).then(res => {
                if (res.data.data?.status === 'active') {
                    stopPolling();
                    setStep('success');
                    load();
                }
            }).catch(() => {});
        }, 3000);
    };

    // Sandbox: Click ilovasidagi tasdiqni simulyatsiya qilish
    const handleSandboxConfirm = () => {
        setPayLoading(true);
        api.post('/click/sandbox-confirm', {
            merchant_trans_id: invoice.merchant_trans_id,
        }).then(res => {
            if (res.data.data?.status === 'active') {
                setStep('success');
                load();
            } else {
                message.warning("To'lov holati: " + (res.data.data?.status || 'noma\'lum'));
            }
        }).catch(err => {
            const msg = err.response?.data?.message || "To'lovni tasdiqlashda xatolik";
            Modal.error({ title: 'Xatolik', content: msg });
        }).finally(() => setPayLoading(false));
    };

    const watchedMonths = Form.useWatch('months', payForm);

    const { services, business } = data;
    const accessibleCount = services.filter(s => s.is_accessible).length;
    const blockedServices = services.filter(s => !s.is_accessible);
    const blockedCount = blockedServices.length;
    const trialCount = services.filter(s => s.in_trial).length;

    const columns = [
        {
            title: 'Xizmat nomi', dataIndex: 'name', key: 'name',
            render: (v, rec) => (
                <div>
                    <Text strong>{v}</Text>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{rec.service_type_name}</Text></div>
                </div>
            ),
        },
        {
            title: 'Holat', key: 'status', width: 180,
            render: (_, rec) => {
                if (rec.in_trial) {
                    return (
                        <div>
                            <Tag icon={<ClockCircleOutlined />} color="blue">Sinov davri</Tag>
                            <div><Text type="secondary" style={{ fontSize: 11 }}>{rec.days_left_trial} kun qoldi</Text></div>
                        </div>
                    );
                }
                if (rec.is_paid) {
                    return (
                        <div>
                            <Tag icon={<CheckCircleOutlined />} color="green">To'langan</Tag>
                            <div><Text type="secondary" style={{ fontSize: 11 }}>{String(rec.active_payment?.paid_to || '').slice(0, 10)} gacha</Text></div>
                        </div>
                    );
                }
                return <Tag icon={<StopOutlined />} color="red">To'lanmagan</Tag>;
            },
        },
        {
            title: 'Tarif', key: 'tariff', width: 160,
            render: (_, rec) => rec.price_monthly ? (
                <Text strong style={{ color: '#1890ff' }}>{formatPrice(rec.price_monthly)} so'm/oy</Text>
            ) : <Text type="secondary">Belgilanmagan</Text>,
        },
        {
            title: 'Sinov tugashi', dataIndex: 'trial_end', key: 'trial_end', width: 130,
            render: (v) => v || '—',
        },
        {
            title: 'Saytda ko\'rinishi', key: 'visible', width: 130,
            render: (_, rec) => rec.is_accessible
                ? <Tag color="green">Ko'rinadi</Tag>
                : <Tag color="red">Yashirin</Tag>,
        },
        {
            title: '', key: 'actions', width: 140,
            render: (_, rec) => (
                <Button
                    type={rec.is_accessible ? 'default' : 'primary'}
                    danger={!rec.is_accessible}
                    icon={<CreditCardOutlined />}
                    size="small"
                    onClick={() => openPay(rec)}
                    disabled={!rec.price_monthly}
                >
                    {rec.is_accessible ? "Uzaytirish" : "To'lash"}
                </Button>
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={4} style={{ margin: 0 }}><DollarOutlined /> Tarif va to'lov holati</Title>
                <Button icon={<ReloadOutlined />} onClick={load}>Yangilash</Button>
            </div>

            {blockedCount > 0 && (
                <Alert
                    type="error"
                    showIcon
                    message={`${blockedCount} ta xizmatingiz to'lanmaganligi sababli saytda ko'rinmayapti!`}
                    description={`Quyidagi xizmatlar uchun to'lovni amalga oshiring: ${blockedServices.map(s => s.name).join(', ')}`}
                    style={{ marginBottom: 16, borderRadius: 10 }}
                />
            )}

            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={12} lg={6}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                        <Statistic title="Jami xizmatlar" value={services.length} />
                    </Card>
                </Col>
                <Col xs={12} lg={6}>
                    <Card size="small" style={{ borderRadius: 10, background: 'rgba(34,197,94,0.14)' }}>
                        <Statistic title="Aktiv" value={accessibleCount} valueStyle={{ color: '#52c41a' }} />
                    </Card>
                </Col>
                <Col xs={12} lg={6}>
                    <Card size="small" style={{ borderRadius: 10, background: '#eff6ff' }}>
                        <Statistic title="Sinov davrida" value={trialCount} valueStyle={{ color: '#1890ff' }} />
                    </Card>
                </Col>
                <Col xs={12} lg={6}>
                    <Card size="small" style={{ borderRadius: 10, background: blockedCount ? '#fef2f2' : '#fafafa' }}>
                        <Statistic title="Bloklangan" value={blockedCount} valueStyle={{ color: blockedCount ? '#ef4444' : '#999' }} />
                    </Card>
                </Col>
            </Row>

            <Card style={{ borderRadius: 12 }}>
                <Table
                    dataSource={services}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    size="middle"
                    pagination={false}
                    locale={{ emptyText: <Empty description="Xizmatlar topilmadi" /> }}
                    rowClassName={(rec) => !rec.is_accessible ? 'ant-table-row-danger' : ''}
                />
            </Card>

            {/* Click Pay modal */}
            <Modal
                title={<span><CreditCardOutlined style={{ marginRight: 8, color: '#1890ff' }} />Click orqali to'lov</span>}
                open={payModal}
                onCancel={closePay}
                footer={null}
                destroyOnClose
                maskClosable={step !== 'pending'}
            >
                {payRecord && (
                    <>
                        <Steps
                            size="small"
                            current={step === 'select' ? 0 : step === 'pending' ? 1 : 2}
                            style={{ marginBottom: 20 }}
                            items={[
                                { title: 'Tanlash' },
                                { title: "To'lov" },
                                { title: 'Tayyor' },
                            ]}
                        />

                        <Card size="small" style={{ marginBottom: 16, borderRadius: 10, background: 'var(--qk-bg-soft)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div>
                                    <Text strong style={{ fontSize: 16 }}>{payRecord.name}</Text>
                                    <div><Text type="secondary">{payRecord.service_type_name}</Text></div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <Text strong style={{ color: '#1890ff', fontSize: 16 }}>{formatPrice(payRecord.price_monthly)} so'm</Text>
                                    <div><Text type="secondary">oyiga</Text></div>
                                </div>
                            </div>
                        </Card>

                        {/* 1-BOSQICH: oy sonini tanlash */}
                        {step === 'select' && (
                            <Form form={payForm} layout="vertical" onFinish={handleStartPayment} requiredMark={false}>
                                <Form.Item name="months" label="Necha oy uchun to'lash" rules={[{ required: true }]}>
                                    <InputNumber min={1} max={12} style={{ width: '100%' }} size="large" />
                                </Form.Item>

                                <Card size="small" style={{ marginBottom: 16, borderRadius: 8, background: 'rgba(34,197,94,0.14)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Text>Jami to'lov:</Text>
                                        <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                                            {formatPrice((watchedMonths || 1) * (payRecord.price_monthly || 0))} so'm
                                        </Text>
                                    </div>
                                </Card>

                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button onClick={closePay}>Bekor</Button>
                                    <Button type="primary" htmlType="submit" loading={payLoading} icon={<CreditCardOutlined />}>
                                        Click orqali to'lash
                                    </Button>
                                </Space>
                            </Form>
                        )}

                        {/* 2-BOSQICH: to'lovni kutish */}
                        {step === 'pending' && invoice && (
                            <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                <Card size="small" style={{ marginBottom: 16, borderRadius: 8, background: 'rgba(34,197,94,0.14)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Text>{invoice.months} oy uchun jami:</Text>
                                        <Text strong style={{ fontSize: 20, color: '#52c41a' }}>{formatPrice(invoice.amount)} so'm</Text>
                                    </div>
                                </Card>

                                {invoice.sandbox ? (
                                    <>
                                        <Alert
                                            type="info"
                                            showIcon
                                            icon={<MobileOutlined />}
                                            message="Test (sandbox) rejimi"
                                            description="Haqiqiy Click ilovasi o'rniga to'lovni quyidagi tugma orqali tasdiqlang. Real rejimda foydalanuvchi Click ilovasida to'lovni tasdiqlaydi."
                                            style={{ marginBottom: 16, textAlign: 'left', borderRadius: 8 }}
                                        />
                                        <Space style={{ width: '100%', justifyContent: 'center' }}>
                                            <Button onClick={closePay}>Bekor</Button>
                                            <Button type="primary" loading={payLoading} icon={<CheckCircleOutlined />} onClick={handleSandboxConfirm}>
                                                To'lovni tasdiqlash (test)
                                            </Button>
                                        </Space>
                                    </>
                                ) : (
                                    <>
                                        <Spin spinning style={{ marginBottom: 16 }} />
                                        <div style={{ marginBottom: 8 }}>
                                            <Text>Click ilovasida to'lovni tasdiqlang…</Text>
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            To'lov tasdiqlangach bu oyna avtomatik yangilanadi.
                                        </Text>
                                        <div style={{ marginTop: 16 }}>
                                            <Button onClick={() => invoice.pay_url && window.open(invoice.pay_url, '_blank', 'noopener')}>
                                                To'lov sahifasini qayta ochish
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* 3-BOSQICH: muvaffaqiyat */}
                        {step === 'success' && (
                            <Result
                                status="success"
                                title="To'lov muvaffaqiyatli!"
                                subTitle={`${payRecord.name} xizmati faollashtirildi va saytda ko'rinadi.`}
                                extra={<Button type="primary" onClick={closePay}>Yopish</Button>}
                            />
                        )}
                    </>
                )}
            </Modal>
        </div>
    );
}
